'use client'

/* Client form for submitting a community photo of a shaheed masjid. Multipart POST to
   /api/masjids/[slug]/shaheed-photos — the server requires sign-in, strips EXIF/GPS, hosts on
   Cloudinary, and stores a PENDING row (never public until a moderator approves). Honest states:
   a 401 shows a sign-in prompt; other errors show the server message; success shows a thank-you. */

import { useRef, useState } from 'react'
import Link from 'next/link'

type Phase = 'idle' | 'submitting' | 'done' | 'signin' | 'error'

export function SharePhotoForm({ slug }: { slug: string }) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [credit, setCredit] = useState('')
  const [permission, setPermission] = useState(false)
  const [year, setYear] = useState('')
  const [caption, setCaption] = useState('')
  const [phase, setPhase] = useState<Phase>('idle')
  const [msg, setMsg] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  function pick(f: File | null) {
    setFile(f)
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return f ? URL.createObjectURL(f) : null })
  }

  const canSubmit = !!file && credit.trim().length > 0 && permission && phase !== 'submitting'

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setMsg('')
    if (!file) { setMsg('Please choose a photo.'); return }
    if (!credit.trim()) { setMsg('Please add a credit name.'); return }
    if (!permission) { setMsg('Please confirm you took this photo or have permission to share it.'); return }
    setPhase('submitting')
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('credit', credit.trim())
      fd.append('permission', 'true')
      if (year.trim()) fd.append('yearTaken', year.trim())
      if (caption.trim()) fd.append('caption', caption.trim())
      const res = await fetch(`/api/masjids/${encodeURIComponent(slug)}/shaheed-photos`, { method: 'POST', body: fd, credentials: 'include' })
      const d = await res.json().catch(() => ({}))
      if (res.status === 401) { setPhase('signin'); return }
      if (!res.ok) { setPhase('error'); setMsg(d.error || `Could not submit (${res.status}).`); return }
      setPhase('done')
    } catch {
      setPhase('error'); setMsg('Network error — please try again.')
    }
  }

  if (phase === 'done') {
    return (
      <div className="shp-note shp-ok">
        <strong>Thank you.</strong> Your photo has been submitted for review. It will appear on the
        record once a moderator approves it.
        <div style={{ marginTop: 12 }}><Link className="shp-back" href={`/masjids/shaheed/${slug}`}>← Back to the record</Link></div>
        <style>{noteStyles}</style>
      </div>
    )
  }

  if (phase === 'signin') {
    return (
      <div className="shp-note shp-warn">
        Please sign in to share a photo.
        <div style={{ marginTop: 12 }}><Link className="shp-back" href={`/login?redirect=/masjids/shaheed/${slug}/share-photo`}>Sign in →</Link></div>
        <style>{noteStyles}</style>
      </div>
    )
  }

  return (
    <form className="shp-form" onSubmit={submit}>
      <label className="shp-field">
        <span>Photo</span>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(e) => pick(e.target.files?.[0] ?? null)} />
      </label>
      {preview ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={preview} alt="Selected photo preview" className="shp-preview" />
      ) : null}

      <label className="shp-field">
        <span>Credit name <b>*</b></span>
        <input value={credit} onChange={(e) => setCredit(e.target.value)} maxLength={120} placeholder="Who took / provided this photo" required />
      </label>

      <label className="shp-field">
        <span>Year taken (optional)</span>
        <input value={year} onChange={(e) => setYear(e.target.value)} maxLength={20} placeholder="e.g. 1991" />
      </label>

      <label className="shp-field">
        <span>Caption (optional)</span>
        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={400} rows={2} placeholder="A short caption for the photo" />
      </label>

      <label className="shp-check">
        <input type="checkbox" checked={permission} onChange={(e) => setPermission(e.target.checked)} />
        <span>I took this photo or have permission to share it.</span>
      </label>

      {msg ? <p className="shp-err">{msg}</p> : null}

      <button type="submit" className="shp-submit" disabled={!canSubmit}>
        {phase === 'submitting' ? 'Submitting…' : 'Submit for review'}
      </button>

      <style>{`
        .shp-form{display:grid;gap:14px}
        .shp-field{display:grid;gap:6px}
        .shp-field>span{font-size:13px;font-weight:700;color:rgba(231,227,214,.85)}
        .shp-field>span b{color:#C9A94D}
        .shp-form input[type=text],.shp-form input:not([type]),.shp-field input,.shp-field textarea{
          background:#0e211b;border:1px solid rgba(231,227,214,.18);border-radius:10px;padding:11px 13px;
          color:#F5F2E8;font-size:14px;font-family:inherit}
        .shp-field input[type=file]{padding:9px 12px;color:rgba(231,227,214,.8)}
        .shp-field textarea{resize:vertical;min-height:52px}
        .shp-field input::placeholder,.shp-field textarea::placeholder{color:rgba(231,227,214,.35)}
        .shp-preview{width:100%;max-height:280px;object-fit:cover;border-radius:12px;border:1px solid rgba(231,227,214,.14)}
        .shp-check{display:flex;align-items:flex-start;gap:10px;font-size:13.5px;line-height:1.5;color:rgba(231,227,214,.85);cursor:pointer}
        .shp-check input{margin-top:2px;width:17px;height:17px;accent-color:#0E7C61}
        .shp-err{color:#fca5a5;font-size:13px;font-weight:600;margin:0}
        .shp-submit{background:#0E7C61;color:#fff;border:none;border-radius:12px;padding:13px 18px;font-size:15px;font-weight:800;cursor:pointer}
        .shp-submit:disabled{opacity:.5;cursor:not-allowed}
      `}</style>
    </form>
  )
}

const noteStyles = `
  .shp-note{border-radius:12px;padding:16px 18px;font-size:14px;line-height:1.6}
  .shp-ok{background:rgba(14,124,97,.14);border:1px solid rgba(14,124,97,.4);color:#E7E3D6}
  .shp-warn{background:rgba(201,169,77,.12);border:1px solid rgba(201,169,77,.4);color:#E7E3D6}
  .shp-back{color:#C9A94D;font-weight:800;text-decoration:none}
`
